import { Grid, Typography } from '@mui/material';
import { FieldErrors, useFormContext } from 'react-hook-form';

import { DatePicker } from '@/components/Form/DatePicker';
import { EmploymentBusinessCategorySelection } from '@/components/Form/Employment/EmploymentBusinessCategorySelection';
import { EmploymentOccupationSelection } from '@/components/Form/Employment/EmploymentOccupationSelection';
import { EmploymentRiskSectorSelection } from '@/components/Form/Employment/EmploymentRiskSectorSelection';
import EmploymentRiskSectorQuestion from '@/components/Form/Employment/EmploymentRiskyQuestion';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';
import { EMPLOYED_TYPE_OPTIONS } from '@/constants/employment';
import { PHONE_PATTERN } from '@/constants/patterns';
import { EMPLOYER_MAX_LENGTH } from '@/constants/places';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectTerritories } from '@/redux/countries/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import { OccupationType } from '@/types/form';

import { isCaymanUser, isGuyanaUser } from '@/utils/user';
import { isFutureDate } from '@/utils/validation';

import { UpdatePersonalInfo } from '../types';

type EmployedFormSectionProps = {
  visibleErrors: FieldErrors<UpdatePersonalInfo>;
};

export function EmployedFormSection({
  visibleErrors,
}: Readonly<EmployedFormSectionProps>) {
  const { t, country } = useTranslate();
  const { control, watch, setValue } = useFormContext<UpdatePersonalInfo>();
  const { user } = useAppSelector(selectUserData);
  const { phoneCodes } = useAppSelector(selectTerritories);
  const { occupations } = useAppSelector(selectConfigsState);
  const { dateFormat } = useAppSelector(selectConfigsState);

  const watchEmploymentDesc = watch('employmentDetailed.employmentDesc');
  const { employmentDetailed } = visibleErrors;

  const countryOptions = COUNTRIES_WITH_POPULAR.map(item => ({
    ...item,
    text: country[item.value],
  }));

  const isCayman = isCaymanUser(user);
  const isGuyana = isGuyanaUser(user);

  return (
    <>
      {isCayman ? (
        <>
          <Typography variant="caption">
            {t('employerForm.form.businessCategory.label')}
          </Typography>
          <EmploymentBusinessCategorySelection<UpdatePersonalInfo>
            control={control}
            name="employmentDetailed.businessCategoryCode"
            label={''}
          />
        </>
      ) : (
        <>
          <Typography variant="caption">
            {t('employerForm.form.employedType.label')}
          </Typography>
          <Select
            options={EMPLOYED_TYPE_OPTIONS.map(option => ({
              value: option.value,
              text: t(option.text),
            }))}
            control={control}
            name="employmentDetailed.employmentDesc"
            error={!!visibleErrors.employmentDetailed?.employmentDesc}
            helperText={
              visibleErrors.employmentDetailed?.employmentDesc?.message
            }
            rules={{
              required: t('employerForm.form.employedType.required'),
            }}
            fullWidth
            placeholder={t('employerForm.form.employedType.placeholder')}
            useDrawerOnMobile
            withSearchBar
            onSelectChange={event => {
              const newEmployedType = event.target.value;
              if (newEmployedType !== watchEmploymentDesc) {
                setValue('employmentDetailed.occupationCode', '');
              }
            }}
          />
        </>
      )}
      <Typography variant="caption">
        {t('updatePersonalInfo.employmentInfoForm.field.employerName.label')}
      </Typography>
      <TextInput
        name="employmentDetailed.employerName"
        control={control}
        error={!!employmentDetailed?.employerName}
        helperText={employmentDetailed?.employerName?.message}
        fullWidth
        rules={{
          required: t(
            'updatePersonalInfo.employmentInfoForm.field.employerName.error.required',
          ),
          maxLength: {
            value: EMPLOYER_MAX_LENGTH.employerName,
            message: t(
              'updatePersonalInfo.employmentInfoForm.field.employerName.error.maxLength',
              {
                number: EMPLOYER_MAX_LENGTH.employerName,
              },
            ),
          },
        }}
      />
      <Typography variant="caption">
        {t('updatePersonalInfo.employmentInfoForm.field.address.label')}
      </Typography>
      <TextInput
        name="employmentDetailed.employerAddress"
        control={control}
        error={!!employmentDetailed?.employerAddress}
        helperText={employmentDetailed?.employerAddress?.message}
        fullWidth
        rules={{
          required: t(
            'updatePersonalInfo.employmentInfoForm.field.address.error.required',
          ),
          maxLength: {
            value: EMPLOYER_MAX_LENGTH.employerAddress,
            message: t(
              'updatePersonalInfo.employmentInfoForm.field.address.error.maxLength',
              {
                number: EMPLOYER_MAX_LENGTH.employerAddress,
              },
            ),
          },
        }}
      />
      <Typography variant="caption">
        {t('employerForm.form.employerCountry.label')}
      </Typography>
      <Select
        options={countryOptions}
        control={control}
        name="employmentDetailed.employerCountry"
        error={!!visibleErrors.employmentDetailed?.employerCountry}
        helperText={visibleErrors.employmentDetailed?.employerCountry?.message}
        rules={{
          required: t('employerForm.form.employerCountry.required'),
        }}
        fullWidth
        placeholder={t('employerForm.form.employerCountry.placeholder')}
        useDrawerOnMobile
        withSearchBar
      />
      <Typography variant="caption">
        {t('updatePersonalInfo.employmentInfoForm.field.phone.label')}
      </Typography>
      <Grid container columnSpacing={1.5} rowSpacing={2}>
        <Grid item xs={5}>
          <Select
            options={phoneCodes}
            control={control}
            name="employmentDetailed.employerPhoneCode"
            fullWidth
          />
        </Grid>
        <Grid item xs={7}>
          <TextInput
            type="tel"
            inputMode="tel"
            name="employmentDetailed.employerPhone"
            placeholder={t(
              'updatePersonalInfo.employmentInfoForm.field.phone.placeholder',
            )}
            control={control}
            error={!!employmentDetailed?.employerPhone}
            helperText={employmentDetailed?.employerPhone?.message}
            inputProps={{ maxLength: 7 }}
            rules={{
              required: t(
                'updatePersonalInfo.employmentInfoForm.field.phone.error.required',
              ),
              pattern: {
                value: new RegExp(PHONE_PATTERN),
                message: t(
                  'updatePersonalInfo.employmentInfoForm.field.phone.error.pattern',
                ),
              },
            }}
          />
        </Grid>
      </Grid>
      <Typography variant="caption">
        {t('updatePersonalInfo.employmentInfoForm.field.startDate.label')}
      </Typography>
      <DatePicker
        name="employmentDetailed.startDate"
        format={dateFormat}
        control={control}
        error={!!employmentDetailed?.startDate}
        helperText={employmentDetailed?.startDate?.message}
        rules={{
          required: t(
            'updatePersonalInfo.employmentInfoForm.field.startDate.error.required',
          ),
          validate: {
            isNotFutureDate: value =>
              isFutureDate(value as string)
                ? t(
                    'updatePersonalInfo.employmentInfoForm.field.startDate.error.cannotBeFuture',
                  )
                : undefined,
          },
        }}
        disableFuture
      />
      <Typography variant="caption">
        {t('updatePersonalInfo.employmentInfoForm.field.occupationCode.label')}
      </Typography>
      {isCayman ? (
        <Select
          options={occupations}
          control={control}
          name="employmentDetailed.occupationCode"
          error={!!employmentDetailed?.occupationCode}
          helperText={employmentDetailed?.occupationCode?.message}
          fullWidth
          placeholder={t(
            'updatePersonalInfo.employmentInfoForm.field.occupationCode.placeholder',
          )}
          rules={{
            required: t(
              'updatePersonalInfo.employmentInfoForm.field.occupationCode.error.required',
            ),
          }}
          useDrawerOnMobile
          withSearchBar
        />
      ) : (
        <EmploymentOccupationSelection
          control={control}
          name="employmentDetailed.occupationCode"
          occupationType={watchEmploymentDesc as OccupationType}
        />
      )}
      {!isCayman && !isGuyana && (
        <EmploymentRiskSectorQuestion
          control={control}
          name="employmentDetailed.riskSectorFlag"
          options={[
            { value: true, label: t('common.yes') },
            { value: false, label: t('common.no') },
          ]}
        />
      )}
      {isGuyana && (
        <EmploymentRiskSectorSelection
          control={control}
          name="employmentDetailed.riskSectorCategory"
        />
      )}
    </>
  );
}
